import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  LinearProgress,
  Chip,
  IconButton,
  Skeleton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CalendarBlank,
  Users,
  ListChecks,
  Calendar,
  ChartPie,
  ArrowRight,
  ChatText,
  Heart,
  Clock,
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudLightning,
  MapPin,
  Warning,
  CloudSun,
  ArrowClockwise,
  Lightbulb,
  Sparkle,
  Buildings,
  DotsThree,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
// Import our component styles hook
import useComponentStyles from '../../theme/useComponentStyles';

function CountdownEditModal({ open, onClose, initialCeremonyDate, currentCountdownImageUrl, onSaved }) {
  const _styles = useComponentStyles();
  const [ceremonyDate, setCeremonyDate] = useState(initialCeremonyDate ? initialCeremonyDate.toISOString().slice(0, 10) : '');
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setCeremonyDate(initialCeremonyDate ? initialCeremonyDate.toISOString().slice(0, 10) : '');
    setImageFile(null);
    setError(null);
  };

  useEffect(() => {
    if (open) {
      setCeremonyDate(initialCeremonyDate ? initialCeremonyDate.toISOString().slice(0, 10) : '');
      setImageFile(null);
      setError(null);
    }
  }, [open, initialCeremonyDate]);

  const handleSave = async () => {
    setError(null);
    setLoading(true);
    try {
      let newImageUrl = currentCountdownImageUrl;
      if (imageFile) {
        const formData = new FormData();
        formData.append('countdownImage', imageFile);
        const uploadRes = await api.post('/api/wedding-details/upload-countdown-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newImageUrl = uploadRes.data.imageUrl;
      }
      const detailsRes = await api.get('/api/wedding-details');
      const venues = detailsRes.data?.venues || {};
      const ceremony = { ...venues.ceremony, date: ceremonyDate ? new Date(ceremonyDate).toISOString() : undefined };
      await api.put('/api/wedding-details/venues', { ...venues, ceremony });
      const savedDate = ceremonyDate ? new Date(ceremonyDate) : null;
      onSaved({ ceremonyDate: savedDate, countdownImageUrl: newImageUrl });
      resetForm();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit countdown</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Wedding date"
            type="date"
            value={ceremonyDate}
            onChange={(e) => setCeremonyDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Countdown card image
            </Typography>
            <Button variant="outlined" component="label" startIcon={<ImageIcon size={20} weight="regular" />} fullWidth>
              {imageFile ? imageFile.name : (currentCountdownImageUrl ? 'Change image' : 'Upload image')}
              <input type="file" accept="image/jpeg,image/png,image/jpg" hidden onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
            </Button>
            {!currentCountdownImageUrl && !imageFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Default image will be used if none is set.
              </Typography>
            )}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { wedding, user } = useAuth();
  // Use our component styles
  const styles = useComponentStyles();
  
  // State for dashboard data - ALL HOOKS MUST BE AT THE TOP
  const [todos, setTodos] = useState({ active: [], completed: [], loading: true });
  const [guests, setGuests] = useState({ total: 0, confirmed: 0, declined: 0, pending: 0, loading: true });
  const [messages, setMessages] = useState({ recent: [], loading: true });
  const [weddingDate, setWeddingDate] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [_venueCity, setVenueCity] = useState('');
  const [dashboardError, setDashboardError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [suggestions, setSuggestions] = useState({ items: [], loading: true });
  const [_weddingProgress, setWeddingProgress] = useState(0);
  const [lastVisited, setLastVisited] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState({ items: [], loading: true });
  const [suppliers, setSuppliers] = useState({ total: 0, loading: true });
  const [countdownImageUrl, setCountdownImageUrl] = useState(null);
  const [countdownModalOpen, setCountdownModalOpen] = useState(false);
  
  // Function to fetch weather data
  const fetchWeatherData = async (address, date) => {
    if (!address) {
      setWeatherLoading(false);
      return { noAddress: true };
    }
    
    try {
      setWeatherLoading(true);

      // Check if date is too far in the future (more than 16 days)
      const today = new Date();
      const daysDiff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

      // Convert address to coordinates using OpenStreetMap Nominatim API
      const encodedAddress = encodeURIComponent(address);

      const geoResponse = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}`);
      const geoData = await geoResponse.json();

      if (!geoData || geoData.length === 0) {
        setWeatherLoading(false);
        return { geocodingFailed: true };
      }

      const { lat, lon } = geoData[0];

      // If date is within forecast range (16 days), use forecast API
      if (daysDiff <= 16) {
        // Format date for API request (YYYY-MM-DD)
        const formattedDate = date.toISOString().split('T')[0];

        // Use Open-Meteo API for weather forecast
        const weatherApiUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&timezone=auto&start_date=${formattedDate}&end_date=${formattedDate}`;

        try {
          const weatherResponse = await fetch(weatherApiUrl);
          const weatherData = await weatherResponse.json();

          // Check if we got valid weather data
          if (!weatherData.daily || !weatherData.daily.weathercode || weatherData.daily.weathercode.length === 0) {
            setWeatherLoading(false);
            return { invalidData: true };
          }

          const weatherCode = weatherData.daily.weathercode[0];
          const maxTemp = weatherData.daily.temperature_2m_max[0];
          const minTemp = weatherData.daily.temperature_2m_min[0];
          const precipitation = weatherData.daily.precipitation_sum[0];

          const weatherInfo = {
            code: weatherCode,
            maxTemp: Math.round(maxTemp),
            minTemp: Math.round(minTemp),
            precipitation: Math.round(precipitation * 10) / 10, // Round to 1 decimal place
            description: getWeatherDescription(weatherCode),
            icon: getWeatherIcon(weatherCode)
          };

          setWeatherData(weatherInfo);
          setWeatherLoading(false);
          return weatherInfo;
        } catch (weatherError) {
          setWeatherLoading(false);
          return { weatherError: weatherError.message };
        }
      } else {
        // Get historical data for the same date in previous years
        const historicalYear = new Date().getFullYear() - 1;
        const historicalDate = new Date(date);
        historicalDate.setFullYear(historicalYear);
        const formattedHistoricalDate = historicalDate.toISOString().split('T')[0];

        const historicalApiUrl = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${formattedHistoricalDate}&end_date=${formattedHistoricalDate}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum`;

        try {
          const historicalResponse = await fetch(historicalApiUrl);
          const historicalData = await historicalResponse.json();

          if (!historicalData.daily || !historicalData.daily.weathercode || historicalData.daily.weathercode.length === 0) {
            setWeatherLoading(false);
            return { invalidHistoricalData: true };
          }

          const weatherCode = historicalData.daily.weathercode[0];
          const maxTemp = historicalData.daily.temperature_2m_max[0];
          const minTemp = historicalData.daily.temperature_2m_min[0];
          const precipitation = historicalData.daily.precipitation_sum[0];

          const weatherInfo = {
            code: weatherCode,
            maxTemp: Math.round(maxTemp),
            minTemp: Math.round(minTemp),
            precipitation: Math.round(precipitation * 10) / 10,
            description: getWeatherDescription(weatherCode),
            icon: getWeatherIcon(weatherCode),
            isHistorical: true
          };

          setWeatherData(weatherInfo);
          setWeatherLoading(false);
          return weatherInfo;
        } catch (historicalError) {
          setWeatherLoading(false);
          return { historicalError: historicalError.message };
        }
      }
    } catch (error) {
      setWeatherLoading(false);
      return { error: error.message };
    }
  };

  // Get weather icon based on weather code
  const getWeatherIcon = (weatherCode) => {
    // WMO Weather interpretation codes (https://open-meteo.com/en/docs)
    if (weatherCode <= 0) { // Clear sky
      return <Sun size={16} weight="fill" color="#FFB300" />;
    } else if (weatherCode <= 3) { // Partly cloudy
      return <CloudSun size={16} weight="fill" color="#FFB300" />;
    } else if (weatherCode <= 49) { // Fog or cloudy
      return <Cloud size={16} weight="fill" color="#78909C" />;
    } else if (weatherCode <= 69) { // Rain
      return <CloudRain size={16} weight="fill" color="#42A5F5" />;
    } else if (weatherCode <= 79) { // Snow
      return <CloudSnow size={16} weight="fill" color="#E0E0E0" />;
    } else { // Thunderstorm
      return <CloudLightning size={16} weight="fill" color="#5C6BC0" />;
    }
  };

  // Get weather description based on weather code
  const getWeatherDescription = (weatherCode) => {
    if (weatherCode <= 0) return "Clear sky";
    if (weatherCode <= 3) return "Partly cloudy";
    if (weatherCode <= 49) return "Foggy/Cloudy";
    if (weatherCode <= 69) return "Rainy";
    if (weatherCode <= 79) return "Snowy";
    return "Thunderstorm";
  };

  // Helper function to get section name from path
  const _getSectionName = (path) => {
    const sectionMap = {
      '/todos': 'To-Do List',
      '/chat': 'Messages',
      '/guest-list': 'Guest List',
      '/wedding-details': 'Wedding Details',
      '/timeline': 'Timeline',
      '/budget': 'Budget Tracker',
      '/partner-details': 'Partner Details',
      '/couple-profile': 'Wedding',
      '/supplier-search': 'Find Suppliers'
    };
    return sectionMap[path] || 'Dashboard';
  };

  // Helper function to get section icon from path
  const _getSectionIcon = (path) => {
    const iconMap = {
      '/todos': ListChecks,
      '/chat': ChatText,
      '/guest-list': Users,
      '/wedding-details': CalendarBlank,
      '/timeline': Calendar,
      '/budget': ChartPie,
      '/partner-details': Heart,
      '/couple-profile': Heart,
      '/supplier-search': Buildings
    };
    return iconMap[path] || ArrowRight;
  };

  // Load last visited section on mount and when wedding changes
  useEffect(() => {
    try {
      const stored = localStorage.getItem('lastVisitedSection');
      if (stored) {
        const data = JSON.parse(stored);
        // Only show if visited within last 7 days and not the current page
        const daysSince = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
        if (daysSince < 7 && data.path !== location.pathname) {
          setLastVisited(data);
        } else {
          setLastVisited(null);
        }
      } else {
        setLastVisited(null);
      }
    } catch (error) {
      setLastVisited(null);
    }
  }, [location.pathname]);
  
  // Note: Wedding requirement is now handled by ProtectedRoute, so no need for this check

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setDashboardError(null);
        
        // Try to fetch todos
        try {
          const todosResponse = await api.get('/api/todos');
          const todosData = todosResponse.data;
          setTodos({
            active: todosData.filter(todo => !todo.completed),
            completed: todosData.filter(todo => todo.completed),
            loading: false
          });
        } catch (error) {
          // Use mock data if API fails
          setTodos({
            active: [
              { _id: '1', title: 'Book venue', completed: false },
              { _id: '2', title: 'Send invitations', completed: false },
              { _id: '3', title: 'Order wedding cake', completed: false },
              { _id: '4', title: 'Book photographer', completed: false },
            ],
            completed: [
              { _id: '5', title: 'Create guest list', completed: true },
              { _id: '6', title: 'Set wedding date', completed: true },
            ],
            loading: false
          });
        }
        
        // Try to fetch guests
        try {
          const guestsResponse = await api.get('/api/guests');
          const guestsData = guestsResponse.data;
          setGuests({
            total: guestsData.length,
            confirmed: guestsData.filter(guest => guest.rsvpStatus === 'confirmed').length,
            declined: guestsData.filter(guest => guest.rsvpStatus === 'declined').length,
            pending: guestsData.filter(guest => guest.rsvpStatus === 'pending' || !guest.rsvpStatus).length,
            loading: false
          });
        } catch (error) {
          // Use mock data if API fails
          setGuests({
            total: 120,
            confirmed: 45,
            declined: 10,
            pending: 65,
            loading: false
          });
        }
        
        // Try to fetch messages
        try {
          const messagesResponse = await api.get('/api/messages/recent');
          setMessages({
            recent: messagesResponse.data.slice(0, 3),
            loading: false
          });
        } catch (error) {
          // Use mock data if API fails
          setMessages({
            recent: [
              { 
                _id: '1', 
                content: 'Hello! I wanted to confirm your booking for the venue.',
                createdAt: new Date().toISOString(),
                sender: { name: 'Venue Manager', profilePicture: '' }
              },
              { 
                _id: '2', 
                content: 'Your cake design looks amazing! Can we discuss the flavors?',
                createdAt: new Date(Date.now() - 86400000).toISOString(),
                sender: { name: 'Cake Designer', profilePicture: '' }
              }
            ],
            loading: false
          });
        }
        
        // Calculate days remaining and countdown image
        setCountdownImageUrl(wedding?.details?.countdownImageUrl ?? null);
        if (wedding?.date) {
          const weddingDate = new Date(wedding.date);
          setWeddingDate(weddingDate);
          
          const today = new Date();
          const timeDiff = weddingDate.getTime() - today.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          setDaysRemaining(daysDiff);
          
          // Fetch weather data if we have a ceremony venue address (venues live in wedding.details)
          let ceremonyVenue = wedding?.details?.venues?.ceremony || wedding?.venues?.ceremony;
          if (!ceremonyVenue?.address) {
            try {
              const detailsRes = await api.get('/api/wedding-details');
              ceremonyVenue = detailsRes.data?.venues?.ceremony;
            } catch (e) {
              ceremonyVenue = null;
            }
          }
          if (ceremonyVenue?.address) {
            if (ceremonyVenue.city) {
              setVenueCity(ceremonyVenue.city);
            } else {
              const addressParts = ceremonyVenue.address.split(',');
              if (addressParts.length >= 2) setVenueCity(addressParts[1].trim());
            }
            let geocodingAddress = ceremonyVenue.address;
            if (ceremonyVenue.addressLine1 && ceremonyVenue.city) {
              geocodingAddress = `${ceremonyVenue.addressLine1}, ${ceremonyVenue.city}`;
              if (ceremonyVenue.county) geocodingAddress += `, ${ceremonyVenue.county}`;
              if (ceremonyVenue.postcode) geocodingAddress += `, ${ceremonyVenue.postcode}`;
            }
            fetchWeatherData(geocodingAddress, weddingDate);
          }
        } else {
          // Try to get the ceremony date from wedding details
          try {
            const detailsRes = await api.get('/api/wedding-details');

            const ceremonyDate = detailsRes.data.venues?.ceremony?.date ? new Date(detailsRes.data.venues.ceremony.date) : null;

            setCountdownImageUrl(detailsRes.data?.countdownImageUrl ?? null);
            if (ceremonyDate) {
              setWeddingDate(ceremonyDate);
              
              const today = new Date();
              const timeDiff = ceremonyDate.getTime() - today.getTime();
              const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
              setDaysRemaining(daysDiff);
              
              // Also try to fetch weather if we have venue address
              if (detailsRes.data.venues && detailsRes.data.venues.ceremony && detailsRes.data.venues.ceremony.address) {
                // Extract city
                if (detailsRes.data.venues.ceremony.city) {
                  setVenueCity(detailsRes.data.venues.ceremony.city);
                } else {
                  const addressParts = detailsRes.data.venues.ceremony.address.split(',');
                  if (addressParts.length >= 2) {
                    setVenueCity(addressParts[1].trim());
                  }
                }

                fetchWeatherData(detailsRes.data.venues.ceremony.address, ceremonyDate);
              }
            } else {
              setDaysRemaining(0);
            }
          } catch (detailsError) {
            // Use mock date if error fetching details
            const mockDate = new Date();
            mockDate.setMonth(mockDate.getMonth() + 6);
            setWeddingDate(mockDate);
            setDaysRemaining(180);
          }
        }

        // Fetch dashboard suggestions
        try {
          const suggestionsResponse = await api.get('/api/dashboard/suggestions');

          if (suggestionsResponse.data && suggestionsResponse.data.suggestions) {
            setSuggestions({
              items: suggestionsResponse.data.suggestions || [],
              loading: false
            });
            setWeddingProgress(suggestionsResponse.data.weddingProgress || 0);
          } else {
            setSuggestions({ items: [], loading: false });
          }
        } catch (error) {
          // Set empty suggestions but don't fail the whole dashboard
          setSuggestions({ items: [], loading: false });
          // Try to get progress from wedding object if available
          if (wedding?.progress?.overall !== undefined) {
            setWeddingProgress(wedding.progress.overall);
          }
        }

        // Fetch upcoming appointments (bookings)
        try {
          const bookingsRes = await api.get('/api/unified-bookings/my-bookings').catch(() => ({ data: { bookings: [] } }));
          
          const appointments = [];
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (bookingsRes.data && bookingsRes.data.bookings && Array.isArray(bookingsRes.data.bookings)) {
            bookingsRes.data.bookings
              .filter(booking => {
                if (!booking.startTime) return false;
                const startDate = new Date(booking.startTime);
                startDate.setHours(0, 0, 0, 0);
                const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
                return daysUntil >= 0 && daysUntil <= 60 && booking.status !== 'cancelled';
              })
              .forEach(booking => {
                const startDate = new Date(booking.startTime);
                startDate.setHours(0, 0, 0, 0);
                const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
                
                appointments.push({
                  id: booking._id,
                  title: booking.serviceType?.name || booking.business?.name || 'Appointment',
                  businessName: booking.business?.name,
                  date: startDate,
                  startTime: booking.startTime,
                  daysUntil,
                  priority: daysUntil <= 7 ? 'high' : daysUntil <= 30 ? 'medium' : 'low',
                  status: booking.status
                });
              });
          }

          // Sort by date (soonest first) and take top 5
          appointments.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
          setUpcomingAppointments({ items: appointments.slice(0, 5), loading: false });
        } catch (error) {
          setUpcomingAppointments({ items: [], loading: false });
        }

        // Fetch suppliers count
        try {
          const suppliersRes = await api.get('/api/user-suppliers').catch(() => ({ data: [] }));
          setSuppliers({ 
            total: Array.isArray(suppliersRes.data) ? suppliersRes.data.length : 0, 
            loading: false 
          });
        } catch (error) {
          setSuppliers({ total: 0, loading: false });
        }

        // Finally set loading to false when all data is fetched
        setIsLoading(false);
      } catch (error) {
        // Set dashboard error for user feedback
        setDashboardError('Failed to load dashboard data. Please try refreshing the page.');
        // Ensure all data is set to non-loading state
        setTodos(prev => ({ ...prev, loading: false }));
        setGuests(prev => ({ ...prev, loading: false }));
        setMessages(prev => ({ ...prev, loading: false }));
        setSuggestions(prev => ({ ...prev, loading: false }));
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [wedding, location.pathname]);

  // Setup loading timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setLoadingTimeout(true);
      }
    }, 12000); // 12-second timeout

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show loading while checking wedding access
  if (user && !wedding) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // If we're in a loading state, show a loading indicator
  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 3 }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  // If we hit a timeout, show a refresh button
  if (loadingTimeout) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <Warning size={40} weight="fill" color={theme.palette.warning.main} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Dashboard taking too long to load
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
          There might be a connection issue
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<ArrowClockwise weight="bold" />}
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  // Show any dashboard errors
  if (dashboardError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          }
        >
          {dashboardError}
        </Alert>
      </Box>
    );
  }

  // Custom card styles based on our component styles
  const dashboardCardStyle = {
    ...styles.cards.default,
    height: '100%',
  };

  // Clickable card style
  const clickableCardStyle = {
    ...dashboardCardStyle,
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      borderColor: 'rgba(0, 0, 0, 0.15)',
    }
  };

  const defaultCountdownImage = '/img/background-wedding-countdown.png';
  const countdownCardBackgroundUrl = countdownImageUrl || user?.profilePicture || defaultCountdownImage;

  // Card with countdown image, profile picture, or pastel fallback
  const cardWithProfileOrPastel = () => {
    const bgImage = countdownCardBackgroundUrl
      ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.7)), url(${countdownCardBackgroundUrl})`
      : null;
    return {
      ...dashboardCardStyle,
      ...(bgImage && { backgroundImage: bgImage, backgroundSize: 'cover', backgroundPosition: 'center' }),
      ...(!bgImage && { bgcolor: theme.palette.primary.light }),
      color: 'white',
    };
  };

  return (
    <Container maxWidth="lg" sx={{ ...styles.containers.pageContainer, px: 0 }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box', mx: 'auto' }}>
        {/* Clean Title and Introduction Area - align with card content (same left inset) */}
        <Box sx={{ mb: 4 }}>
          <Typography component="h2" className="pagename" sx={styles.text.pagename}>
            Welcome Back, {user?.firstName || 'User'}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 400, mb: 2, color: 'text.secondary' }}>
            {wedding?.name || 'Your Wedding Dashboard'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px' }}>
            Track key metrics like tasks, appointments, and progress to stay on top of your wedding planning.
          </Typography>
        </Box>

        {/* Continue Where You Left Off - full width on mobile to align with grid */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1, display: 'none' }}>
            {/* Hidden for minimalist design */}
          </Box>

          {lastVisited && (
            <Card
              sx={{
                ...styles.cards.default,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 200 },
                maxWidth: { xs: 'none', sm: 280 },
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.02)
                }
              }}
              onClick={() => navigate(lastVisited.path)}
            >
              <CardContent sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                    Continue
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                    {lastVisited.name}
                  </Typography>
                </Box>
                <ArrowRight size={16} weight="bold" color={theme.palette.primary.main} />
              </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Dashboard Grid - no column gap on mobile (centred); spacing on desktop */}
        <Grid container rowSpacing={{ xs: 2, sm: 3 }} columnSpacing={{ xs: 0, sm: 3 }} sx={{ width: '100%', maxWidth: '100%' }}>
        {/* ROW 1: Countdown (50%) and Upcoming Appointments (50%) */}
        <Grid item xs={12} sm={6} md={6}>
          <Card sx={{ ...cardWithProfileOrPastel(), position: 'relative' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: { xs: 160, sm: 220 } }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setCountdownModalOpen(true); }}
                sx={{ position: 'absolute', top: 8, right: 8, color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                aria-label="Edit countdown"
              >
                <DotsThree size={24} weight="bold" />
              </IconButton>
              {weddingDate ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontSize: '0.875rem', textAlign: 'center' }}>
                    {weddingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </Typography>
                  <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, fontSize: '5.5rem !important', textAlign: 'center' }}>
                    {daysRemaining}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2, opacity: 0.9, textAlign: 'center' }}>
                    Days Until Your Wedding
                  </Typography>
                  {weatherData && !weatherLoading && weatherData.code !== undefined ? (
                    <Box sx={{ ...styles.layout.flexCenter, mt: 1 }}>
                      {getWeatherIcon(weatherData.code)}
                      <Typography variant="body2" sx={{ ml: 1, textAlign: 'center' }}>
                        {weatherData.isHistorical ? 'Typically ' : ''}
                        {weatherData.description}
                        {' • '}
                        {weatherData.minTemp} to {weatherData.maxTemp}°C
                        {weatherData.precipitation > 0 && ` • ${weatherData.precipitation} mm`}
                      </Typography>
                    </Box>
                  ) : weatherLoading ? (
                    <Box sx={{ ...styles.layout.flexCenter, mt: 1 }}>
                      <CircularProgress size={14} sx={{ color: 'white' }} />
                      <Typography variant="body2" sx={{ ml: 1, textAlign: 'center' }}>Loading weather...</Typography>
                    </Box>
                  ) : weatherData && (weatherData.geocodingFailed || weatherData.noAddress) ? (
                    <Box sx={{ ...styles.layout.flexCenter, mt: 1 }}>
                      <MapPin size={16} weight="regular" />
                      <Typography variant="body2" sx={{ ml: 1, textAlign: 'center' }}>Add venue address for weather</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ ...styles.layout.flexCenter, mt: 1 }}>
                      <Sun size={16} weight="regular" />
                      <Typography variant="body2" sx={{ ml: 1, textAlign: 'center' }}>
                        {(wedding?.details?.venues?.ceremony?.address || wedding?.venues?.ceremony?.address) ? 'Fetching weather data...' : 'Weather data will appear here'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setCountdownModalOpen(true)}
                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.25)', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' } }}
                  >
                    Start your countdown
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
          <CountdownEditModal
            open={countdownModalOpen}
            onClose={() => setCountdownModalOpen(false)}
            initialCeremonyDate={weddingDate || (wedding?.details?.venues?.ceremony?.date ? new Date(wedding.details.venues.ceremony.date) : null)}
            currentCountdownImageUrl={countdownImageUrl}
            onSaved={({ ceremonyDate, countdownImageUrl: newUrl }) => {
              if (ceremonyDate) {
                setWeddingDate(ceremonyDate);
                const today = new Date();
                const daysDiff = Math.ceil((ceremonyDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
                setDaysRemaining(daysDiff);
              }
              if (newUrl !== undefined) setCountdownImageUrl(newUrl);
            }}
          />
        </Grid>

        {/* Upcoming Appointments Card */}
        <Grid item xs={12} sm={6} md={6}>
          <Card 
            sx={clickableCardStyle}
            onClick={() => navigate('/bookings')}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  Upcoming Appointments
                </Typography>
                <Calendar size={24} weight="regular" color={theme.palette.info.main} />
              </Box>
              
              {upcomingAppointments.loading ? (
                <>
                  <Skeleton animation="wave" height={20} width="80%" />
                  <Skeleton animation="wave" height={20} width="70%" />
                  <Skeleton animation="wave" height={20} width="85%" />
                </>
              ) : upcomingAppointments.items.length > 0 ? (
                <>
                  {upcomingAppointments.items.slice(0, 4).map((appointment, index) => {
                    const appointmentDate = new Date(appointment.startTime);
                    const timeStr = appointmentDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    const dateStr = appointmentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    
                    return (
                      <Box 
                        key={appointment.id || index}
                        sx={{ 
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: index < upcomingAppointments.items.slice(0, 4).length - 1 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                              {appointment.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {appointment.businessName && `${appointment.businessName} • `}
                              {dateStr} at {timeStr}
                            </Typography>
                          </Box>
                          <Chip 
                            label={appointment.daysUntil === 0 ? 'Today' : 
                                   appointment.daysUntil === 1 ? 'Tomorrow' :
                                   appointment.daysUntil <= 7 ? 'Soon' : 'Upcoming'}
                            size="small"
                            color={appointment.priority === 'high' ? 'error' : appointment.priority === 'medium' ? 'warning' : 'default'}
                            sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                  {upcomingAppointments.items.length > 4 && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block', textAlign: 'right' }}>
                      +{upcomingAppointments.items.length - 4} more
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  No upcoming appointments
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ROW 2: What to Do Next, Todo List, Messages (3 columns) */}
        {/* What to Do Next Card */}
        <Grid item xs={12} sm={4} md={4}>
          <Card sx={dashboardCardStyle}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  What to Do Next
                </Typography>
                <Sparkle size={20} weight="regular" color={theme.palette.warning.main} />
              </Box>
              
              {suggestions.loading ? (
                <>
                  <Skeleton animation="wave" height={20} width="80%" />
                  <Skeleton animation="wave" height={20} width="70%" />
                </>
              ) : suggestions.items.length > 0 ? (
                <>
                  {suggestions.items.slice(0, 2).map((suggestion, index) => {
                    const IconComponent = suggestion.icon === 'ListChecks' ? ListChecks :
                                         suggestion.icon === 'Users' ? Users :
                                         suggestion.icon === 'Calendar' ? Calendar :
                                         suggestion.icon === 'ChartPie' ? ChartPie :
                                         suggestion.icon === 'Buildings' ? Buildings :
                                         suggestion.icon === 'Heart' ? Heart :
                                         suggestion.icon === 'Clock' ? Clock : Lightbulb;
                    
                    return (
                      <Box 
                        key={index}
                        sx={{ 
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: suggestion.priority === 'high' ? 'error.light' : 
                                   suggestion.priority === 'medium' ? 'warning.light' : 
                                   'info.light',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: suggestion.priority === 'high' ? 'error.main' : 
                                     suggestion.priority === 'medium' ? 'warning.main' : 
                                     'info.main',
                            color: 'white',
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => suggestion.action?.path && navigate(suggestion.action.path)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <IconComponent size={18} weight="regular" sx={{ mr: 1, mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} gutterBottom sx={{ fontSize: '0.875rem' }}>
                              {suggestion.title}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              {suggestion.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                  No suggestions
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* To-Do List Card */}
        <Grid item xs={12} sm={4} md={4}>
          <Card 
            sx={clickableCardStyle}
            onClick={() => navigate('/todos')}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                  To-Do List
                </Typography>
                <ListChecks size={20} weight="regular" color={theme.palette.text.primary} />
              </Box>
              
              {todos.loading ? (
                <>
                  <Skeleton animation="wave" height={30} width="60%" />
                  <Skeleton animation="wave" height={20} width="80%" />
                </>
              ) : (
                <>
                  <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: theme.palette.primary.main }}>
                    {todos.active.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Active Tasks
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={todos.completed.length > 0 ? (todos.completed.length / (todos.active.length + todos.completed.length)) * 100 : 0} 
                    sx={{ mb: 2, height: 6, borderRadius: 3 }}
                  />
                  
                  {todos.active.length > 0 && (
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.875rem' }}>
                      {todos.active[0].title}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Messages Card */}
        <Grid item xs={12} sm={4} md={4}>
          <Card 
            sx={clickableCardStyle}
            onClick={() => navigate('/chat')}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: theme.palette.info.main }}>
                  Messages
                </Typography>
                <ChatText size={20} weight="regular" color={theme.palette.info.main} />
              </Box>
              
              {messages.loading ? (
                <>
                  <Skeleton animation="wave" height={30} width="90%" />
                  <Skeleton animation="wave" height={20} width="80%" />
                </>
              ) : messages.recent.length > 0 ? (
                <>
                  <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: theme.palette.info.main }}>
                    {messages.recent.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Recent Messages
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.875rem' }}>
                    {messages.recent[0]?.sender?.name || 'Supplier'}: {messages.recent[0]?.content?.substring(0, 40)}...
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: theme.palette.info.main }}>
                    0
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No Recent Messages
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ROW 3: Guest List and Supplier List (2 columns) */}
        {/* Guest List Card */}
        <Grid item xs={12} sm={6} md={6}>
          <Card 
            sx={clickableCardStyle}
            onClick={() => navigate('/guest-list')}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: theme.palette.primary.main }}>
                  Guest List
                </Typography>
                <Users size={24} weight="regular" color={theme.palette.primary.main} />
              </Box>
              
              {guests.loading ? (
                <Skeleton animation="wave" height={60} width="80%" />
              ) : (
                <>
                  <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: theme.palette.primary.main }}>
                    {guests.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Total Guests
                  </Typography>
                  
                  <Grid container spacing={1} sx={{ mt: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Confirmed</Typography>
                      <Typography variant="h6" color="success.main">{guests.confirmed}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Pending</Typography>
                      <Typography variant="h6" color="warning.main">{guests.pending}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">Declined</Typography>
                      <Typography variant="h6" color="error.main">{guests.declined}</Typography>
                    </Grid>
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Supplier List Card */}
        <Grid item xs={12} sm={6} md={6}>
          <Card 
            sx={clickableCardStyle}
            onClick={() => navigate('/suppliers')}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: theme.palette.primary.main }}>
                  Supplier List
                </Typography>
                <Buildings size={24} weight="regular" color={theme.palette.primary.main} />
              </Box>
              
              {suppliers.loading ? (
                <Skeleton animation="wave" height={60} width="80%" />
              ) : (
                <>
                  <Typography variant="h2" sx={{ mb: 1, fontWeight: 700, color: theme.palette.primary.main }}>
                    {suppliers.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    My Suppliers
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Manage your suppliers and view bookings
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ROW 4: Budget Tracker */}
        <Grid item xs={12} sm={12} md={12}>
          <Card 
            sx={clickableCardStyle}
            onClick={() => navigate('/budget')}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={styles.layout.flexBetween}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: theme.palette.info.main }}>
                    Budget Tracker
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Track your wedding expenses and stay within budget.
                  </Typography>
                </Box>
                <ChartPie size={32} weight="regular" color={theme.palette.info.main} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default Dashboard; 